import React, { useEffect, useState, useRef } from 'react';
import Layout from './Layout';
import { defaultAxiosInstance } from '../api/defaultInstance';
import { useUser } from '../context/UserContext';
import { useNavigate, useLocation } from 'react-router-dom';
import '../css/ReferralManagement.css';

function ReferralManagement() {
    const { userInfo } = useUser();
    // data-scope: D-14 — partner는 자기 데이터만 보는 표시·필터 범위다(기능 권한 판단 아님, 권한 시스템 범위 밖)
    const isPartnerScope = userInfo?.role === 'partner'; // data-scope: D-14
    const navigate = useNavigate();
    const location = useLocation();
    const isRestoringFromHistory = useRef(false);
    const [isLoading, setIsLoading] = useState(false);
    const [list, setList] = useState([]);
    const [summary, setSummary] = useState({
        membership_reward: 0,
        reward_10: 0,
        reward_5_root: 0,
        total_invitation_count: 0,
        total_conversion_count: 0
    });
    const [filter, setFilter] = useState({
        startMonth: '',
        endMonth: '',
        email: '',
        name: '',
        parentUserName: '',
        referralCode: '',
        userId: '',
        page: 1,
        limit: 20
    });
    const [totalCount, setTotalCount] = useState(0);
    const [selectedRow, setSelectedRow] = useState(null);

    // 데이터 조회
    const fetchData = async (currentFilter = filter, updateSummary = true) => {
        setIsLoading(true);
        try {
            const params = {
                page: currentFilter.page || 1,
                limit: currentFilter.limit || 20
            };

            if (currentFilter.startMonth) params.startMonth = currentFilter.startMonth;
            if (currentFilter.endMonth) params.endMonth = currentFilter.endMonth;
            if (currentFilter.email) params.email = currentFilter.email;
            if (currentFilter.name) params.name = currentFilter.name;
            if (currentFilter.parentUserName) params.parentUserName = currentFilter.parentUserName;
            if (currentFilter.referralCode) params.referralCode = currentFilter.referralCode;
            if (currentFilter.userId) params.userId = currentFilter.userId;

            const response = await defaultAxiosInstance.get('/advertisement/admin/referral-management/list', {
                params
            });

            if (response.data.status === 200) {
                const data = response.data.data;
                setList(data.list || []);
                setTotalCount(data.totalCount || 0);
                // partner인 경우 행 클릭 시 summary 업데이트하지 않음
                console.log("updateSummary2: ", updateSummary);

                if (updateSummary) {
                    setSummary(data.summary || {
                        membership_reward: 0,
                        reward_10: 0,
                        reward_5_root: 0,
                        total_invitation_count: 0,
                        total_conversion_count: 0
                    });
                }
            }
        } catch (error) {
            console.error('데이터 조회 오류:', error);
            alert('데이터 조회 중 오류가 발생했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        // 초기 날짜 설정 (현재 월)
        const now = new Date();
        const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        const initialFilter = {
            startMonth: currentMonth,
            endMonth: currentMonth,
            email: '',
            name: isPartnerScope ? (userInfo?.name || '') : '',
            parentUserName: '',
            referralCode: '',
            userId: '',
            page: 1,
            limit: 20
        };
        
        // location.state에서 필터 복원 시도 (뒤로가기/앞으로가기 시)
        if (location.state && location.state.filter) {
            const savedFilter = location.state.filter;
            isRestoringFromHistory.current = true;
            setFilter(savedFilter);
            setSelectedRow(savedFilter.parentUserName ? { name: savedFilter.parentUserName } : null);
            fetchData(savedFilter);
        } else {
            setFilter(initialFilter);
            fetchData(initialFilter);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInfo, location.state]);

    useEffect(() => {
        if (filter.startMonth && filter.endMonth) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter.page]);

    const handleFilterChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            setFilter(prev => ({ ...prev, [name]: checked }));
        } else {
            setFilter(prev => ({ ...prev, [name]: value }));
        }
    };

    const applyFilter = () => {
        const filterToApply = { ...filter, page: 1 };
        setFilter(filterToApply);
        
        // 필터 적용 시 히스토리에 추가 (복원 중이 아닐 때만)
        if (!isRestoringFromHistory.current) {
            navigate(location.pathname, { 
                state: { filter: filterToApply },
                replace: false 
            });
        } else {
            isRestoringFromHistory.current = false;
        }
        
        fetchData(filterToApply);
    };

    const handleRowClick = (row) => {
        // partner인 경우 parentUserName이 있으면 클릭 불가
        if (isPartnerScope && filter.parentUserName) {
            return;
        }
        setSelectedRow(row);
        const updatedFilter = {
            ...filter,
            name: '',
            parentUserName: row.name,
            page: 1
        };
        setFilter(updatedFilter);
        
        // 행 클릭 시 히스토리에 추가 (복원 중이 아닐 때만)
        if (!isRestoringFromHistory.current) {
            navigate(location.pathname, { 
                state: { filter: updatedFilter },
                replace: false 
            });
        } else {
            isRestoringFromHistory.current = false;
        }
        
        // user_id로 필터해서 리스트 반환
        // partner인 경우 summary 업데이트하지 않음
        const shouldUpdateSummary = !isPartnerScope;
        console.log("shouldUpdateSummary: ", shouldUpdateSummary);
        fetchData(updatedFilter, shouldUpdateSummary);
    };

    const clearFilter = () => {
        const now = new Date();
        const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        const clearedFilter = {
            startMonth: currentMonth,
            endMonth: currentMonth,
            email: '',
            name: isPartnerScope ? (userInfo?.name || '') : '',
            parentUserName: '',
            referralCode: '',
            userId: '',
            page: 1,
            limit: 20
        };
        setFilter(clearedFilter);
        setSelectedRow(null);
        
        // 필터 초기화 시 히스토리에 추가 (복원 중이 아닐 때만)
        if (!isRestoringFromHistory.current) {
            navigate(location.pathname, { 
                state: { filter: clearedFilter },
                replace: false 
            });
        } else {
            isRestoringFromHistory.current = false;
        }
        
        fetchData(clearedFilter);
    };

    const handlePrevPage = () => {
        const newPage = Math.max(1, filter.page - 1);
        const updatedFilter = { ...filter, page: newPage };
        setFilter(updatedFilter);
        
        // 페이지 변경 시 히스토리에 추가 (복원 중이 아닐 때만)
        if (!isRestoringFromHistory.current) {
            navigate(location.pathname, { 
                state: { filter: updatedFilter },
                replace: false 
            });
        } else {
            isRestoringFromHistory.current = false;
        }
        
        // API 호출
        fetchData(updatedFilter);
    };

    const handleNextPage = () => {
        const newPage = filter.page + 1;
        const updatedFilter = { ...filter, page: newPage };
        setFilter(updatedFilter);
        
        // 페이지 변경 시 히스토리에 추가 (복원 중이 아닐 때만)
        if (!isRestoringFromHistory.current) {
            navigate(location.pathname, { 
                state: { filter: updatedFilter },
                replace: false 
            });
        } else {
            isRestoringFromHistory.current = false;
        }
        
        // API 호출
        fetchData(updatedFilter);
    };

    const handlePageClick = (page) => {
        const updatedFilter = { ...filter, page };
        setFilter(updatedFilter);
        
        // 페이지 변경 시 히스토리에 추가 (복원 중이 아닐 때만)
        if (!isRestoringFromHistory.current) {
            navigate(location.pathname, { 
                state: { filter: updatedFilter },
                replace: false 
            });
        } else {
            isRestoringFromHistory.current = false;
        }
        
        // API 호출
        fetchData(updatedFilter);
    };

    const startIndex = (filter.page - 1) * filter.limit + 1;
    const endIndex = Math.min(filter.page * filter.limit, totalCount);
    const totalPages = Math.ceil(totalCount / filter.limit);

    return (
        <Layout>
            <div className="referral-management-container">
                {/* 요약 카드 */}
                <div className={`summary ${isPartnerScope ? 'partner-summary' : ''}`}>
                    <div className="summary-box">
                        누적 초대 수<br />
                        <strong>{summary.total_invitation_count?.toLocaleString() || 0}</strong>
                    </div>
                    <div className="summary-box">
                        누적 전환 수<br />
                        <strong>{summary.total_conversion_count?.toLocaleString() || 0}</strong>
                    </div>
                    <div className="summary-box">
                        멤버십 정산액<br />
                        <strong>{summary.membership_reward?.toLocaleString() || 0}원</strong>
                    </div>
                    <div className="summary-box">
                        10% 정산액<br />
                        <strong>{summary.reward_10?.toLocaleString() || 0}원</strong>
                    </div>
                    <div className="summary-box">
                        강사 5% 정산액<br />
                        <strong>{summary.reward_5_root?.toLocaleString() || 0}원</strong>
                    </div>
                    {!isPartnerScope && (
                        <div className="summary-box">
                            강사 외 5% 정산액<br />
                            <strong>{summary.reward_5_parent?.toLocaleString() || 0}원</strong>
                        </div>
                    )}
                </div>

                {/* 필터 섹션 */}
                <div className="filter-bar">
                    {/* 첫 번째 줄: 조회 기간 설정만 */}
                    <div className="filter-group">
                        <label>조회 기간 설정</label>
                        <div className="date-range">
                            <input
                                type="checkbox"
                                name="allPeriod"
                                id="allPeriod"
                                checked={!filter.startMonth && !filter.endMonth}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        setFilter(prev => ({ ...prev, startMonth: '', endMonth: '' }));
                                    } else {
                                        const now = new Date();
                                        const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                                        setFilter(prev => ({ ...prev, startMonth: currentMonth, endMonth: currentMonth }));
                                    }
                                }}
                            />
                            <label htmlFor="allPeriod">전체</label>
                            <input
                                type="month"
                                name="startMonth"
                                value={filter.startMonth}
                                onChange={handleFilterChange}
                                disabled={!filter.startMonth && !filter.endMonth}
                            />
                            <span>~</span>
                            <input
                                type="month"
                                name="endMonth"
                                value={filter.endMonth}
                                onChange={handleFilterChange}
                                disabled={!filter.startMonth && !filter.endMonth}
                            />
                        </div>
                    </div>
                    {/* 두 번째 줄: 상위 유저, 코드, 아이디, 이름, 조회 버튼 */}
                    <div className="filter-group">
                        <label>상위 유저</label>
                        <input
                            type="text"
                            name="parentUserName"
                            placeholder="유저명"
                            value={filter.parentUserName}
                            onChange={handleFilterChange}
                            disabled={isPartnerScope}
                            onKeyDown={e => { if (e.key === 'Enter') applyFilter(); }}
                        />
                    </div>
                    {!isPartnerScope && (
                        <div className="filter-group">
                            <label>코드</label>
                            <input
                                type="text"
                                name="referralCode"
                                placeholder="코드 입력"
                                value={filter.referralCode}
                                onChange={handleFilterChange}
                                onKeyDown={e => { if (e.key === 'Enter') applyFilter(); }}
                            />
                        </div>
                    )}
                    {!isPartnerScope && (
                        <div className="filter-group">
                            <label>아이디</label>
                            <input
                                type="text"
                                name="email"
                                placeholder="아이디 입력"
                                value={filter.email}
                                onChange={handleFilterChange}
                                onKeyDown={e => { if (e.key === 'Enter') applyFilter(); }}
                            />
                        </div>
                    )}
                    {!isPartnerScope && (
                        <div className="filter-group">
                            <label>이름</label>
                            <input
                                type="text"
                                name="name"
                                placeholder="이름 입력"
                                value={filter.name}
                                onChange={handleFilterChange}
                                onKeyDown={e => { if (e.key === 'Enter') applyFilter(); }}
                            />
                        </div>
                    )}
                    <div className="filter-group query-button-group">
                        <button onClick={applyFilter} className="query-button">
                            조회
                        </button>
                        {selectedRow && !isPartnerScope && (
                            <button onClick={clearFilter} className="clear-button">
                                필터 초기화
                            </button>
                        )}
                    </div>
                </div>

                {/* 테이블 */}
                <div className="table-container">
                    <table>
                        <thead>
                            <tr>
                                {!isPartnerScope && <th>아이디</th>}
                                <th>이름</th>
                                <th>가입일</th>
                                <th>지급(예정) 포인트</th>
                                <th>활성 사용자 수</th>
                                <th>활성 베이직 수</th>
                                <th>활성 스탠다드 수</th>
                                <th>활성 프리미엄 수</th>
                                <th>하위 캠페인 구매액</th>
                                <th>내 캠페인 구매액</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan={isPartnerScope ? "8" : "9"} className="center-cell">로딩 중...</td>
                                </tr>
                            ) : list.length === 0 ? (
                                <tr>
                                    <td colSpan={isPartnerScope ? "8" : "9"} className="center-cell">데이터가 없습니다.</td>
                                </tr>
                            ) : (
                                list.map((item, idx) => {
                                    const isClickable = !(isPartnerScope && filter.parentUserName);
                                    return (
                                    <tr
                                        key={item.user_id || idx}
                                        onClick={isClickable ? () => handleRowClick(item) : undefined}
                                        className={selectedRow?.user_id === item.user_id ? 'selected-row' : (isClickable ? 'clickable-row' : '')}
                                    >
                                        {!isPartnerScope && <td>{item.email || ''}</td>}
                                        <td>{item.name || ''}</td>
                                        <td>{item.created_at || ''}</td>
                                        <td>{item.reward_amount_sum?.toLocaleString() || 0}</td>
                                        <td>{item.invitation_count?.toLocaleString() || 0}</td>
                                        <td>{item.basic_conversion_count?.toLocaleString() || 0}</td>
                                        <td>{item.standard_conversion_count?.toLocaleString() || 0}</td>
                                        <td>{item.premium_conversion_count?.toLocaleString() || 0}</td>
                                        <td>{item.campaign_purchase_amount?.toLocaleString() || 0}원</td>
                                        <td>{item.my_ad_complete_purchase_amount?.toLocaleString() || 0}원</td>
                                    </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* 페이징 */}
                <div className="pagination-info">
                    <div>총 {totalCount}개 항목 중 {startIndex} - {endIndex}</div>
                    <div className="pagination">
                        <button onClick={handlePrevPage} disabled={filter.page === 1 || isLoading}>이전</button>
                        {totalPages <= 10 ? (
                            Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                <button
                                    key={page}
                                    className={filter.page === page ? "active" : ""}
                                    onClick={() => handlePageClick(page)}
                                    disabled={isLoading}
                                >
                                    {page}
                                </button>
                            ))
                        ) : (
                            <>
                                {filter.page > 1 && <button onClick={() => handlePageClick(1)} disabled={isLoading}>1</button>}
                                {filter.page > 2 && <span>...</span>}
                                {filter.page > 0 && <button className="active" disabled>{filter.page}</button>}
                                {filter.page < totalPages - 1 && <span>...</span>}
                                {filter.page < totalPages && <button onClick={() => handlePageClick(totalPages)} disabled={isLoading}>{totalPages}</button>}
                            </>
                        )}
                        <button onClick={handleNextPage} disabled={endIndex >= totalCount || isLoading}>다음</button>
                    </div>
                </div>
            </div>
        </Layout>
    );
}

export default ReferralManagement;

